// StoryViewerScreen.js

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './StoryViewerScreen.css';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import dotsIcon from '../assets/dots-icon.png';
import { useDiscovery } from '../context/DiscoveryContext';
import { markSeen } from '../utils/storySeen';
import sendIcon from '../assets/send-icon.png';
import goldTickIcon from '../assets/goldtick-icon.png';

// 💸 tips
import tipIcon from '../assets/tip-icon.png';
import TipDrawer from '../components/popups/TipDrawer';
import TipListDrawer from '../components/popups/TipListDrawer';

// 👀 views (owner)
import ViewersDrawer from '../components/popups/ViewersDrawer';

import StoryDeleteDrawer from '../components/popups/StoryDeleteDrawer';
import StoryMenuDrawer from '../components/popups/StoryMenuDrawer';

// 🔌 swipe-to-next-user feed order
import { fetchActiveStoryUsers } from "../utils/stories";

// -------------------------
// 🔍 DEBUG LOGGER
// -------------------------
const DEBUG = true;
const tag = '🟣[StoryViewer]';

const DEFAULT_TEXT_BACKGROUND_KEY = 'mint';

const STORY_TEXT_BACKGROUNDS = {
  coral: {
    backgroundColor: '#ef746f',
    textColor: '#ffffff',
  },

  mint: {
    backgroundColor: '#74bea0',
    textColor: '#ffffff',
  },

  blue: {
    backgroundColor: '#4f83d6',
    textColor: '#ffffff',
  },

  purple: {
    backgroundColor: '#8064c4',
    textColor: '#ffffff',
  },

  dark: {
    backgroundColor: '#25282e',
    textColor: '#ffffff',
  },
};

function getTextStoryTheme(backgroundKey) {
  const normalized = String(
    backgroundKey || ''
  )
    .trim()
    .toLowerCase();

  return (
    STORY_TEXT_BACKGROUNDS[normalized] ||
    STORY_TEXT_BACKGROUNDS[
      DEFAULT_TEXT_BACKGROUND_KEY
    ]
  );
}

const log = (...args) => {
  if (!DEBUG) return;
  // use log (not debug) so it always shows
  console.log(`${tag} ${new Date().toISOString()}:`, ...args);
};

function StoryViewerScreen() {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔄 user feed order for swipe navigation
  const [feedUsers, setFeedUsers] = useState([]);
  const [feedCursor, setFeedCursor] = useState(null);
  const [uIdx, setUIdx] = useState(-1); // index of current username in feedUsers

  // 👆 swipe tracking
  const swipeStartXRef = useRef(0);
  const swipeStartYRef = useRef(0);
  const isSwipingRef = useRef(false);

  const didSwipeRef = useRef(false);
  const lastDxRef = useRef(0);
  const lastDyRef = useRef(0);


   // ✨ page-level (user-to-user) slide animation (whole screen)
 const [pageAnimPhase, setPageAnimPhase] = useState('idle'); // 'idle'|'out'|'in'
 const [pageAnimDir, setPageAnimDir] = useState('left');     // 'left'|'right'
 const isPageAnimatingRef = useRef(false);
 const PAGE_MS = 220;
 const pageAnimTimerRef = useRef(null);


// session "finished owners" -> skip repeating within this viewer session
const FINISHED_KEY = 'bf_story_session_finished_v1';
const finishedSetRef = useRef(new Set());
const saveFinished = () => {
  try {
    const arr = Array.from(finishedSetRef.current);
    sessionStorage.setItem(FINISHED_KEY, JSON.stringify(arr));
  } catch {}
};
useEffect(() => {
  // hydrate on mount
  try {
    const raw = sessionStorage.getItem(FINISHED_KEY);
    if (raw) finishedSetRef.current = new Set(JSON.parse(raw));
  } catch {}
}, []);
const markOwnerFinishedLocally = (uname) => {
  if (!uname) return;
  finishedSetRef.current.add(uname.toLowerCase());
  saveFinished();
};
const isFinished = (uname) => finishedSetRef.current.has(String(uname || '').toLowerCase());


  // detect if we navigated here directly after publishing a story
  const cameFromPublish = location.state?.from === 'publish';

  // --- network selection (route param wins, else hostname) ---
  const routeNetwork = React.useMemo(() => {
    const p = new URLSearchParams(location.search);
    const n = (p.get('network') || '').toLowerCase();
    return n === 'testnet' || n === 'mainnet' ? n : '';
  }, [location.search]);

  const effectiveNetwork = React.useMemo(() => {
    if (routeNetwork) return routeNetwork;
    if (typeof window !== 'undefined' && window.location.hostname.includes('testnet')) return 'testnet';
    return 'mainnet';
  }, [routeNetwork]);

  // Slides = one per story doc (or per legacy item, read-only)
  const [slides, setSlides] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

// 🆕 Full-screen loading while deciding where to go
  const [initialLoading, setInitialLoading] = useState(true);


  // 🔹 NEW: owner mongo _id for mark-seen
  const [ownerId, setOwnerId] = useState(null);

  // segmented progress
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0); // 0..100 of active segment
  const [paused, setPaused] = useState(false);

  // viewer comment state
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);

  // UI (menu)
  const [showStoryMenuDrawer, setShowStoryMenuDrawer] = useState(false);

  // timers
  const timerRef = useRef(null);

  // track which doc we last rendered to decide when to reset progress
  const lastDocIdRef = useRef(null);

  // Mark-once guard
  const markedRef = useRef(false);

  const [showDeleteDrawer, setShowDeleteDrawer] = useState(false);

  // Get current user (same key you use in App.js)
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('barterfi_user') || 'null'); }
    catch { return null; }
  }, []);
  const isOwner = !!currentUser && currentUser.username === username;

  // --- time-ago formatter (min/hr/day) ---
  const formatTimeAgo = (dateish) => {
    const ts = (typeof dateish === 'string' || dateish instanceof Date)
      ? new Date(dateish).getTime()
      : Number(dateish);
    if (!ts || Number.isNaN(ts)) return '';
    const diff = Math.max(0, Date.now() - ts);

    const s = Math.floor(diff / 1000);
    if (s < 60) return 'just now';

    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min`;

    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hr`;

    const d = Math.floor(h / 24);
    return `${d}d`;
  };

  // live tick so label updates every minute
  const [timeAgoNow, setTimeAgoNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setTimeAgoNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // 💸 tips state (DECLARE BEFORE using in drawerOpen)
  const [showTipPopup, setShowTipPopup] = useState(false);
  const [showTipListDrawer, setShowTipListDrawer] = useState(false);
  const [tipList, setTipList] = useState([]);
  const [tipCount, setTipCount] = useState(0);
  const [tipMessage, setTipMessage] = useState('');
  const [tipType, setTipType] = useState(''); // 'success' | 'error'

  // 👀 views drawer state (owner)
  const [showViewersDrawer, setShowViewersDrawer] = useState(false);

  // Input focus (to pause & hide while keyboard is up)
  const [inputFocused, setInputFocused] = useState(false);

  // Derived: any drawer open?
  const drawerOpen = useMemo(
    () => showTipPopup || showTipListDrawer || showViewersDrawer || showDeleteDrawer || showStoryMenuDrawer,
    [showTipPopup, showTipListDrawer, showViewersDrawer, showDeleteDrawer, showStoryMenuDrawer]
  );

  // Pause whenever any overlay/focus is active
  useEffect(() => {
    setPaused(drawerOpen || inputFocused || document.hidden);
  }, [drawerOpen, inputFocused]);

  // Revalidate discovery row after delete / seen
  const { refetch: refetchDiscovery } = useDiscovery();


  // 🔔 Mount / Unmount trace
  useEffect(() => {
    log('MOUNT', { routeUser: username, cameFromPublish, search: location.search, effectiveNetwork });

 // cleanup any previous timers and reset guards on username change
 try { clearTimeout(pageAnimTimerRef.current); } catch {}
 isPageAnimatingRef.current = false;
 setPageAnimPhase('idle');

 // If previous page passed a direction, animate the whole screen in
 const ef = location.state?.enterFrom;
 if (ef === 'left' || ef === 'right') {
   setPageAnimDir(ef);
   setPageAnimPhase('in');

     pageAnimTimerRef.current = setTimeout(() => {
     setPageAnimPhase('idle');
     isPageAnimatingRef.current = false; // ✅ allow future swipes again
   }, PAGE_MS);

 }

return () => {
    log('UNMOUNT', { routeUser: username });

   try { clearTimeout(pageAnimTimerRef.current); } catch {}
  };

}, [username, cameFromPublish, location.search, effectiveNetwork]);


 const goToUserWithPageAnim = (targetUsername, dir /* 'left'|'right' */) => {
   if (!targetUsername || isPageAnimatingRef.current) return;
   isPageAnimatingRef.current = true;
   setPageAnimDir(dir);
   setPageAnimPhase('out');
   const search = location?.search || "";

 try { clearTimeout(pageAnimTimerRef.current); } catch {}
 pageAnimTimerRef.current = setTimeout(() => {

     navigate(`/stories/${targetUsername}${search}`, {
      replace: true,
       state: { enterFrom: dir === 'left' ? 'right' : 'left' },
     });
     // new page mounts fresh; no reset needed here
   }, PAGE_MS);
 };




  // Normalize server docs → slides


const normalizeDocsToSlides = (docs) => {
  const result = [];

  docs.forEach((doc) => {
    const items = Array.isArray(doc.items)
      ? doc.items
      : [];

    if (items.length <= 1) {
      const item = items[0] || {
        contentType: doc.contentType,
        imageUrl: doc.imageUrl,
        videoUrl: doc.videoUrl,
        text: doc.text,
        backgroundKey: doc.backgroundKey,
        durationSec: 10,
      };

      const contentType =
        item.contentType ||
        doc.contentType;

      const storyText =
        contentType === 'text'
          ? String(
              item.text ||
              doc.text ||
              ''
            ).trim()
          : '';

      const caption =
        contentType !== 'text'
          ? String(
              doc.text || ''
            ).trim()
          : '';

      result.push({
        _docId: doc._id,
        _canDelete: true,

        contentType,

        imageUrl:
          item.imageUrl ||
          doc.imageUrl ||
          '',

        videoUrl:
          item.videoUrl ||
          doc.videoUrl ||
          '',

        storyText,
        caption,

        backgroundKey:
          item.backgroundKey ||
          doc.backgroundKey ||
          DEFAULT_TEXT_BACKGROUND_KEY,

        durationSec: Number(
          item.durationSec || 10
        ),

        viewsCount:
          doc.viewsCount || 0,

        createdAt:
          doc.createdAt,
      });

      return;
    }

    // Older multi-item story documents.
    items.forEach((item, idx) => {
      const contentType =
        item.contentType;

      const storyText =
        contentType === 'text'
          ? String(
              item.text ||
              doc.text ||
              ''
            ).trim()
          : '';

      const caption =
        contentType !== 'text'
          ? String(
              doc.text || ''
            ).trim()
          : '';

      result.push({
        _docId: doc._id,
        _legacyMulti: true,
        _legacyIndex: idx,
        _canDelete: false,

        contentType,

        imageUrl:
          item.imageUrl || '',

        videoUrl:
          item.videoUrl || '',

        storyText,
        caption,

        backgroundKey:
          item.backgroundKey ||
          doc.backgroundKey ||
          DEFAULT_TEXT_BACKGROUND_KEY,

        durationSec: Number(
          item.durationSec || 10
        ),

        viewsCount:
          doc.viewsCount || 0,

        createdAt:
          item.createdAt ||
          doc.createdAt,
      });
    });
  });

  return result;
};


// Fetch ALL active story docs for the user





  useEffect(() => {
    const fetchActive = async () => {
      log('fetchActive:start', { username });
      setInitialLoading(true);  // 🆕 start loader

      try {
        const res = await api.get(`/stories/${username}/active`);
        const docs = res.data.stories || [];
        log('fetchActive:success', { count: docs.length, docIds: docs.map(d => d._id) });

        // 🆕 If no active stories: go to profile/user instead of trying to render
        if (!docs.length) {
          log('fetchActive:empty -> no active stories, go to profile/user');

          const isOwnerRoute =
            currentUser?.username &&
            currentUser.username.toLowerCase() === String(username || '').toLowerCase();

          if (isOwnerRoute) {
            navigate('/profile', { replace: true });
          } else {
            navigate(`/user/${username}`, { replace: true });
          }
          return; // component will unmount, no need to clear loading
        }

        const normalized = normalizeDocsToSlides(docs);

        // Safety: if normalization produced nothing, treat as no story
        if (!normalized.length) {
          log('fetchActive:normalized-empty -> go to profile/user');

          const isOwnerRoute =
            currentUser?.username &&
            currentUser.username.toLowerCase() === String(username || '').toLowerCase();

          if (isOwnerRoute) {
            navigate('/profile', { replace: true });
          } else {
            navigate(`/user/${username}`, { replace: true });
          }
          return;
        }

        setSlides(normalized);
        setUserInfo(res.data.user);
        setActiveIndex(0);
        setProgress(0);
        setPaused(false);
        markedRef.current = false;

        // 🔹 NEW: resolve ownerId once
        try {
          const u = await api.get(`/users/${username}`);
          setOwnerId(u.data?._id || null);
          log('ownerId:resolved', { ownerId: u.data?._id });
        } catch (e) {
          setOwnerId(null);
          log('ownerId:resolve_failed', e?.message || e);
        }

        setInitialLoading(false); // 🆕 finished successfully

      } catch (err) {
        log('fetchActive:error', err?.response?.data || err?.message || err);
        // Fallback: try latest (legacy path)
        try {
          const latest = await api.get(`/stories/${username}/latest`);
          const s = latest.data.story || {};
          const docsLike = [s];
          log('fallback:latest', { storyId: s?._id });

          const normalized = normalizeDocsToSlides(docsLike);

          if (!normalized.length) throw new Error('No story items');

          setSlides(normalized);
          setUserInfo(latest.data.user);
          setActiveIndex(0);
          setProgress(0);
          setPaused(false);
          markedRef.current = false;

          // 🔹 NEW: resolve ownerId in fallback too
          try {
            const u = await api.get(`/users/${username}`);
            setOwnerId(u.data?._id || null);
            log('ownerId:resolved(fallback)', { ownerId: u.data?._id });
          } catch (e) {
            setOwnerId(null);
            log('ownerId:resolve_failed(fallback)', e?.message || e);
          }

          setInitialLoading(false); // 🆕 finished via fallback

        } catch (err2) {
          // 🆕 If latest also fails: go to profile/user, NOT /home
          log('fallback:failed -> no story, go to profile/user', err2?.message || err2);

          const isOwnerRoute =
            currentUser?.username &&
            currentUser.username.toLowerCase() === String(username || '').toLowerCase();

          if (isOwnerRoute) {
            navigate('/profile', { replace: true });
          } else {
            navigate(`/user/${username}`, { replace: true });
          }
        }
      }
    };
    fetchActive();
  }, [username, navigate, currentUser?.username]);




// Load global active-story owners (cursor paginated) for swipe mode
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      log('feed:load:start(active-users)', { routeUser: username });
      try {
        const { users = [], nextCursor = null } = await fetchActiveStoryUsers({ cursor: 0, limit: 200 });
        if (!mounted) return;

        // shape down to what we need; filter session-finished owners
        const list = users
          .map(u => ({ username: u.username, userId: u.userId, latestStoryAt: u.latestStoryAt }))
          .filter(u => !!u.username && !isFinished(u.username));

        // ensure current route user exists at least once at the front (so we can index it)
        let arr = list;
        let ix = arr.findIndex(u => u.username === username);
        if (ix === -1 && username) {
          arr = [{ username }, ...list];
          ix = 0;
          log('feed:ensure-current-in-list', { injectedAt: 0 });
        }

        setFeedUsers(arr);
        setFeedCursor(nextCursor); // number | null
        setUIdx(ix);

        log('feed:load:success', {
          total: arr.length,
          indexOfCurrent: ix,
          prev: arr[ix - 1]?.username || null,
          next: arr[ix + 1]?.username || null,
          nextCursor,
        });
      } catch (e) {
        log('feed:load:error(active-users)', e?.message || e);
        setFeedUsers([{ username }]);
        setUIdx(0);
        setFeedCursor(null);
      }
    };

    load();
    return () => { mounted = false; };
  }, [username]);



const loadMoreFeedIfNeeded = async () => {
  if (feedCursor == null) return false; // no more pages
  try {
    const { users = [], nextCursor = null } = await fetchActiveStoryUsers({ cursor: feedCursor, limit: 200 });
    const add = users
      .map(u => ({ username: u.username, userId: u.userId, latestStoryAt: u.latestStoryAt }))
      .filter(u => !!u.username && !isFinished(u.username));
    if (add.length === 0) {
      setFeedCursor(nextCursor);
      return Boolean(nextCursor);
    }
    setFeedUsers(prev => {
      // dedupe by username, keep order
      const seen = new Set(prev.map(x => x.username));
      const merged = [...prev, ...add.filter(x => !seen.has(x.username))];
      return merged;
    });
    setFeedCursor(nextCursor);
    return true;
  } catch (e) {
    log('feed:loadMore:error', e?.message || e);
    return false;
  }
};





  // 🔎 Swipe feed state changes (make sure this prints)
  useEffect(() => {
    log('feed:state', {
      route: username,
      uIdx,
      total: feedUsers.length,
      prev: feedUsers[uIdx - 1]?.username || null,
      next: feedUsers[uIdx + 1]?.username || null,
    });
  }, [username, uIdx, feedUsers]);

  // Newest timestamp across currently loaded slides (ms)
  const newestTs = useMemo(() => {
    if (!slides.length) return 0;
    const ts = Math.max(
      ...slides.map(s => {
        const t = new Date(s.createdAt).getTime();
        return isNaN(t) ? 0 : t;
      })
    );
    return ts;
  }, [slides]);

  // Helper: mark all as seen once (only after last slide)
  const markAllSeenOnce = async (why = 'unknown') => {
    if (markedRef.current) { log('markSeen:skip(already-marked)', { why }); return; }
    if (!currentUser?.username || !username || !newestTs) { log('markSeen:skip(missing-data)', { currentUser: !!currentUser, username, newestTs }); return; }

    log('markSeen:start', { viewer: currentUser.username, ownerUsername: username, newestTs, why });
    try {
      // 1) local cache → keeps hasSeenAll() logic instant
      markSeen(currentUser.username, username, newestTs);

      // 2) server bulk mark (viewer only; skip if you are the owner)
      if (!isOwner && ownerId) {
        await api.post(`/stories/owners/${ownerId}/mark-seen`);
        log('markSeen:server-ok', { ownerId });
      } else {
        log('markSeen:server-skip', { isOwner, ownerId });
      }

      markedRef.current = true;


// mark as finished for this session and prune from local feed
markOwnerFinishedLocally(username);
setFeedUsers(prev => prev.filter(u => (u?.username || '').toLowerCase() !== username.toLowerCase()));



      // 3) free a seat immediately in the discovery row (optimistic)
      try {
        window.dispatchEvent(new CustomEvent('bf:storyOwnerFinished', { detail: { username } }));
        log('markSeen:dispatch bf:storyOwnerFinished');
      } catch (e) {
        log('markSeen:dispatch failed', e?.message || e);
      }

      // 4) refetch discovery (ensures backfill with another unseen user)
      if (refetchDiscovery) {
        await refetchDiscovery();
        log('markSeen:refetchDiscovery:done');
      }
    } catch (err) {
      log('markSeen:error', err?.message || err);
    }
  };

  // ✅ Only mark as seen if ALL slides have been viewed (i.e., on last slide)
  const viewedAll = useMemo(
    () => slides.length > 0 && activeIndex >= slides.length - 1,
    [slides.length, activeIndex]
  );

const current =
  slides[activeIndex] || null;

const currentTextTheme = useMemo(() => {
  return getTextStoryTheme(
    current?.backgroundKey
  );
}, [current?.backgroundKey]);

const durationMs = useMemo(() => {

    const sec = Number(current?.durationSec || 10);
    return Math.max(1000, Math.min(60000, sec * 1000)); // clamp 1..60s
  }, [current?.durationSec]);

  // Tip count (per story) — same pattern as Post.js
  useEffect(() => {
    const load = async () => {
      if (!current?._docId) return;
      try {
        const res = await api.get(`/tips/count/${current._docId}`);
        setTipCount(res.data?.count || 0);
        log('tips:count', { storyId: current._docId, count: res.data?.count || 0 });
      } catch {
        setTipCount(0);
        log('tips:count:error', { storyId: current._docId });
      }
    };
    load();
  }, [current?._docId]);

  const timeAgoLabel = useMemo(() => {
    return current?.createdAt ? formatTimeAgo(current.createdAt) : '';
  }, [current?.createdAt, timeAgoNow]);

  const displayName = useMemo(() => {
    return userInfo?.username || userInfo?.name || '';
  }, [userInfo]);

  const storyOwnerUsername = useMemo(() => {
    return userInfo?.username || username || '';
  }, [userInfo?.username, username]);


  const isAppAccount = userInfo?.accountType === 'app';
  const isAdminAccount = userInfo?.userType === 'admin' || !!userInfo?.adminRole;
  const ownerAdminRole = userInfo?.adminRole || null;


  // Open the story owner's profile
  const goToOwnerProfile = (e) => {
    e?.stopPropagation(); // don't trigger the tap-to-advance
    if (!storyOwnerUsername) return;
    log('nav:openProfile', { storyOwnerUsername, isOwner });
    if (isOwner) {
      navigate('/profile');
    } else {
      navigate(`/user/${storyOwnerUsername}`);
    }
  };

  const buildStoryCard = () => {
    const toStoryMediaType = (ct) => {
      if (ct === 'image') return 'story_image';
      if (ct === 'video') return 'story_video';
      return 'story_text';
    };

    return {
      _id: current?._docId,

content:
  current?.contentType === 'text'
    ? current?.storyText || ''
    : current?.caption || '',

      username: userInfo?.username || username,
      name: userInfo?.name || '',
      profilePic: userInfo?.profilePic || '',
      media: current?.imageUrl ? [current.imageUrl] : [],
      mediaType: toStoryMediaType(current?.contentType),
      timestamp: current?.createdAt || new Date()
    };
  };

  // progress loop (auto-advance) — pause should NOT reset progress
  useEffect(() => {
    if (!current) return;

    const docId = current._docId || current._legacyIndex || current.createdAt || activeIndex;
    if (docId !== lastDocIdRef.current) {
      lastDocIdRef.current = docId;
      setProgress(0);
      log('slide:change', { docId: current._docId, idx: activeIndex, type: current.contentType, durationMs });
    }

    clearInterval(timerRef.current);
    if (paused) {
      log('slide:paused', { idx: activeIndex, progress });
      return; // just stop ticking; keep progress where it was
    }

    const stepMs = 100;
    const stepPct = (stepMs / durationMs) * 100;

    timerRef.current = setInterval(() => {
      setProgress(prev => {
        const next = prev + stepPct;
        if (next >= 100) {
          clearInterval(timerRef.current);
          log('slide:complete -> goNext', { idx: activeIndex });
          goNext();
          return 100;
        }
        return next;
      });
    }, stepMs);

    return () => clearInterval(timerRef.current);
  }, [activeIndex, durationMs, paused, current?._docId]);

  // Visibility pause that respects drawers & input focus
  useEffect(() => {
    const onVis = () => {
      const p = document.hidden || drawerOpen || inputFocused;
      log('visibility:change', { hidden: document.hidden, drawerOpen, inputFocused, pausedNext: p });
      setPaused(p);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [drawerOpen, inputFocused]);

  // ✅ Mark on global exits ONLY if all viewed
  useEffect(() => {
    const onBeforeUnload = () => { if (viewedAll) { log('lifecycle:beforeunload'); markAllSeenOnce('beforeunload'); } };
    const onPageHide     = () => { if (viewedAll) { log('lifecycle:pagehide'); markAllSeenOnce('pagehide'); } };

    const onPopState     = () => {
      if (viewedAll) { log('lifecycle:popstate'); markAllSeenOnce('popstate'); }


 // Always exit to home on system back / edge swipe
 log('nav:popstate -> /home');
 navigate('/home', { replace: true });
    };

    const onVisibility   = () => { if (document.hidden && viewedAll) { log('lifecycle:visibility-hidden'); markAllSeenOnce('visibility-hidden'); } };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('popstate', onPopState);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('popstate', onPopState);
      document.removeEventListener('visibilitychange', onVisibility);
      if (viewedAll) { log('lifecycle:unmount -> markSeen'); markAllSeenOnce('unmount'); }
    };
  }, [viewedAll, cameFromPublish, navigate]);

  // 👁️ Record a unique view per DOC when its first slide shows (non-owner)
  const viewedDocIdsRef = useRef(new Set());
  useEffect(() => {
    const send = async () => {
      const id = current?._docId;
      if (!id || isOwner) return;
      if (viewedDocIdsRef.current.has(id)) return;
      viewedDocIdsRef.current.add(id);
      try {
        await api.post(`/stories/${id}/view`);
        log('view:post', { storyId: id });
      } catch (e) {
        log('view:post:error', e?.message || e);
      }
    };
    send();
  }, [current?._docId, isOwner]);

  // 👁️ Owner-only: refresh view count when switching slides
  useEffect(() => {
    const refresh = async () => {
      const id = current?._docId;
      if (!id || !isOwner) return;
      try {
        const resp = await api.get(`/stories/${id}/views`);
        if (typeof resp?.data?.viewsCount === 'number') {
          setSlides(prev =>
            prev.map(s => (s._docId === id ? { ...s, viewsCount: resp.data.viewsCount } : s))
          );
          log('views:refresh', { storyId: id, viewsCount: resp.data.viewsCount });
        }
      } catch (e) {
        log('views:refresh:error', e?.message || e);
      }
    };
    refresh();
  }, [current?._docId, isOwner]);

const finishAndClose = async (reason) => {
    log('finishAndClose:attempt', { reason, activeIndex, totalSlides: slides.length, viewedAll });
    if (activeIndex >= slides.length - 1 && slides.length > 0) {
      await markAllSeenOnce(reason); // last slide only
    }

    // try to move to next owner if possible; else go home
    const tryGoNext = async () => {
      let ni = uIdx + 1;

      // skip any session-finished owners
      while (ni < feedUsers.length && isFinished(feedUsers[ni]?.username)) ni += 1;

      if (ni < feedUsers.length) {
        const target = feedUsers[ni]?.username;
        if (target) {
          goToUserWithPageAnim(target, 'left');
          return true;
        }
      }

      // need to load more pages?
      const loaded = await loadMoreFeedIfNeeded();
      if (loaded) {
        // after load, compute the next valid index again
        ni = uIdx + 1;
        while (ni < feedUsers.length && isFinished(feedUsers[ni]?.username)) ni += 1;
        const target = feedUsers[ni]?.username;
        if (target) {
          goToUserWithPageAnim(target, 'left');
          return true;
        }
      }
      return false;
    };

    const hopped = await tryGoNext();
    if (!hopped) {
      log('finishAndClose:navigate -> /home');
      navigate('/home', { replace: true });
    }
  };

  const goNext = () => {
    if (activeIndex < slides.length - 1) {
      log('slide:goNext', { from: activeIndex, to: activeIndex + 1, total: slides.length });
      setActiveIndex(i => i + 1);
    } else {
      log('slide:last -> finishAndClose(auto-advance-last)');
      finishAndClose('auto-advance-last');
    }
  };

  // ⬅️ Never close on tap-left; just do nothing if we're on the first slide
  const goPrev = () => {
    if (activeIndex > 0) {
      log('slide:goPrev', { from: activeIndex, to: activeIndex - 1 });
      setActiveIndex(i => i - 1);
    } else {
      log('slide:goPrev:no-op(first)');
    }
  };

  // 👉 navigate to next/prev user (keeps query string like ?network=mainnet)
  const goToUserByOffset = (offset) => {

if (!Array.isArray(feedUsers) || feedUsers.length === 0 || uIdx < 0) {

      log('nav:userByOffset:blocked', { reason: 'no-feed-or-index', offset, uIdx, total: feedUsers?.length || 0 });
      return;
    }

let ni = uIdx + offset;

    const tryNavigate = (idx) => {
      const item = feedUsers[idx];
      if (!item) return false;
      if (isFinished(item.username)) return false;
      const search = location?.search || "";
      log('nav:userByOffset:go', {

        fromIdx: uIdx,
        toIdx: idx,
        fromUser: feedUsers[uIdx]?.username || null,
        toUser: item.username,
        search
      });
      goToUserWithPageAnim(item.username, offset > 0 ? 'left' : 'right');
      return true;
    };

    // walk until we find a non-finished owner inside current page
    while (ni >= 0 && ni < feedUsers.length && isFinished(feedUsers[ni]?.username)) {
      ni += offset > 0 ? 1 : -1;
    }
    if (ni >= 0 && ni < feedUsers.length && tryNavigate(ni)) return;

    // if off the array edge, try load more and then navigate
    const loadAndGo = async () => {
      const ok = await loadMoreFeedIfNeeded();
      if (!ok) {
        log('nav:userByOffset:blocked', { reason: 'no-more-pages', offset, uIdx, total: feedUsers.length });
        return;
      }
      // after loading, compute the next index again
      let idx = (offset > 0) ? (feedUsers.length ? feedUsers.length - 1 : 0) : 0;
      // for simplicity, try uIdx+offset again first
      idx = uIdx + offset;
      while (idx >= 0 && idx < feedUsers.length && isFinished(feedUsers[idx]?.username)) {
        idx += offset > 0 ? 1 : -1;
      }
      if (idx >= 0 && idx < feedUsers.length) tryNavigate(idx);
    };
    loadAndGo();
 };



  const swipeToNextUser = () => goToUserByOffset(+1);
  const swipeToPrevUser = () => goToUserByOffset(-1);

  const suppressClickRef = useRef(false);

  // Tap zones: left = prev, right = next — but NEVER close the viewer on tap
  const onTap = (e) => {
    if (suppressClickRef.current) return; // ignore click after swipe
    const path = e.composedPath?.() || [];
    if (path.some(el => el?.classList?.contains('no-tap'))) return;

    const x = e.clientX ?? (e.touches?.[0]?.clientX);
    if (typeof x !== 'number') return;
    const mid = window.innerWidth / 2;

    if (x < mid) {
      log('tap:left', { activeIndex, total: slides.length });
      if (activeIndex > 0) setActiveIndex(i => i - 1);
      return;
    } else {
      log('tap:right', { activeIndex, total: slides.length });
      if (activeIndex < slides.length - 1) {
        setActiveIndex(i => i + 1);
      }
      return;
    }
  };

// long-press pause (mouse & touch)
const onHoldStart = () => {
  log('hold:start', { idx: activeIndex, progress });
  setPaused(true);
};

const onHoldEnd = () => {
  const shouldPause = drawerOpen || inputFocused || document.hidden;
  log('hold:end', {
    idx: activeIndex,
    progress,
    drawerOpen,
    inputFocused,
    shouldPause,
  });
  setPaused(shouldPause);
};


  // ⬅️➡️ detect horizontal swipe while preserving long-press pause


const onTouchStartCombined = (e) => {
  // ❌ Don’t start swipe/hold logic if a drawer is open or input focused
  if (drawerOpen || inputFocused) return;
  if (pageAnimPhase !== 'idle' || isPageAnimatingRef.current) return;

  onHoldStart(); // existing pause behavior
  try {
    const t = e.touches?.[0];
    if (t) {
      swipeStartXRef.current = t.clientX;
      swipeStartYRef.current = t.clientY;
      isSwipingRef.current = true;
      log('swipe:start', { x: t.clientX, y: t.clientY });
    }
  } catch {}
};


 const onTouchMoveCombined = (e) => {

// 🚫 Ignore swipe while drawer open or input focused
  if (drawerOpen || inputFocused) return;
  if (pageAnimPhase !== 'idle' || isPageAnimatingRef.current) return;

    if (!isSwipingRef.current) return;
    const t = e.touches?.[0];
    if (!t) return;

    const dx = t.clientX - swipeStartXRef.current;
    const dy = t.clientY - swipeStartYRef.current;
    lastDxRef.current = dx;
    lastDyRef.current = dy;

    const H_THRESHOLD = 40;

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= H_THRESHOLD) {
      if (!didSwipeRef.current) log('swipe:armed', { dx, dy, H_THRESHOLD });
      didSwipeRef.current = true; // arm swipe
    }
  };



  const onTouchEndCombined = () => {
 // 🚫 Don’t process end/swipe if drawer open or input focused
  if (drawerOpen || inputFocused) {
    onHoldEnd(); // will keep paused if needed
    return;
  }
if (pageAnimPhase !== 'idle' || isPageAnimatingRef.current) return;

    onHoldEnd();
    if (!isSwipingRef.current) return;
    isSwipingRef.current = false;

    if (!didSwipeRef.current) {
      log('swipe:end:no-swipe', { lastDx: lastDxRef.current, lastDy: lastDyRef.current });
      return;
    }
    didSwipeRef.current = false;

    suppressClickRef.current = true; // prevent onClick on this frame
    setTimeout(() => (suppressClickRef.current = false), 0);

const dx = lastDxRef.current;
if (dx < 0) {
  // always jump to next user on right-to-left swipe
  log('swipe:NEXT(user)', { dx, atSlide: activeIndex, total: slides.length });
  swipeToNextUser();
} else {
  // always jump to previous user on left-to-right swipe
  log('swipe:PREV(user)', { dx, atSlide: activeIndex, total: slides.length });
  swipeToPrevUser();
}


  };

  const onTouchCancelCombined = () => {
if (drawerOpen || inputFocused) {
    onHoldEnd();
    isSwipingRef.current = false;
    didSwipeRef.current = false;
    return;
  }
if (pageAnimPhase !== 'idle' || isPageAnimatingRef.current) return;

    onHoldEnd();
    isSwipingRef.current = false;
    didSwipeRef.current = false;
    log('swipe:cancel');
  };

  // open the custom confirm drawer
  const handleDelete = () => {
    log('delete:openDrawer', { currentDocId: current?._docId });
    setShowDeleteDrawer(true);
  };

  // actually delete after user confirms
  const performDelete = async () => {
    const id = current?._docId;
    if (!id) return;
    log('delete:start', { id });
    try {
      await api.delete(`/stories/${id}`);

      let filtered;
      let nextIndex = activeIndex;

      setSlides(prev => {
        filtered = prev.filter(s => s._docId !== id);
        if (filtered.length === 0) return [];
        if (activeIndex >= filtered.length) nextIndex = filtered.length - 1;
        setTimeout(() => {
          setActiveIndex(Math.max(0, nextIndex));
          setProgress(0);
        }, 0);
        return filtered;
      });

      if (refetchDiscovery) refetchDiscovery();

      setTimeout(() => {
        if (!filtered || filtered.length === 0) {
          log('delete:after -> exit viewer (no slides left)');


         navigate('/home', { replace: true });

        } else {
          log('delete:after -> remain', { remaining: filtered.length, nextIndex });
        }
      }, 0);
    } catch (err) {
      log('delete:error', err?.response?.data || err?.message);
      alert('Could not delete story. Please try again.');
    }
  };


  if (initialLoading || !userInfo || !slides.length || !current) {
    log('render:loading', {
      initialLoading,
      hasUserInfo: !!userInfo,
      slidesLen: slides.length,
      hasCurrent: !!current,
    });

    return (
      <div className="story-loading-screen">
        <div className="story-spinner" />
      </div>
    );
  }



  // Tip list: try story endpoint first, fallback to post endpoint (if server reuses it)
  const openTipList = async () => {
    try {
      if (!current?._docId) return;
      let res;
      try {
        res = await api.get(`/tips/story/${current._docId}`);
        log('tips:list:story', { storyId: current._docId, count: res.data?.tips?.length || 0 });
      } catch {
        res = await api.get(`/tips/post/${current._docId}`);
        log('tips:list:post-fallback', { storyId: current._docId, count: res.data?.tips?.length || 0 });
      }
      setTipList(res.data?.tips || []);
    } catch (err) {
      log('tips:list:error', err?.message || err);
      setTipList([]);
    } finally {
      setShowTipListDrawer(true);
    }
  };

  // Send tip — mirror Post.js payload/flow
  const handleSendTip = async ({ amount, message }) => {
    const amt = Number(amount);
    if (!isFinite(amt) || amt <= 0) {
      setTipMessage('Enter a valid amount.');
      setTipType('error');
      setTimeout(() => { setTipMessage(''); setTipType(''); }, 4000);
      return;
    }

    try {
      if (!current?._docId || !currentUser?.username) return;
      log('tips:send:start', { storyId: current._docId, amount: amt });

      const payload = {
        tipper: currentUser.username,
        storyId: current._docId,
        amount: amt,
        message,
      };

      const res = await api.post(`/tips/send?network=${effectiveNetwork}`, payload);
      setTipList(prev => [...prev, res.data?.tip].filter(Boolean));
      setTipCount(prev => prev + 1);

      if (currentUser.username !== storyOwnerUsername) {
        await api.post('/notify', {
          type: 'tip',
          fromUser: currentUser.username,
          toUser: storyOwnerUsername,
          postId: null,
          storyId: current._docId,
          text: `${currentUser.username} tipped your story with ${amt} Pi`,
        });
      }

      setTipMessage(`Tip of ${amt} Pi sent successfully!`);
      setTipType('success');
      log('tips:send:success', { storyId: current._docId });
    } catch (err) {
      const errorMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        '❌ Failed to send tip. Try again.';

      log('tips:send:error', errorMsg);

      const lower = String(errorMsg).toLowerCase();
      if (lower.includes('you do not have') && lower.includes('wallet')) {
        setTipMessage('You do not have an active wallet to create tip.');
      } else if (lower.includes('cannot receive tips') || lower.includes('recipient') || lower.includes('to receive')) {
        setTipMessage(`@${storyOwnerUsername} does not have an active wallet to receive tips.`);
      } else if (lower.includes('insufficient') || lower.includes('not enough')) {
        setTipMessage('Not enough Pi to send this tip.');
      } else {
        setTipMessage(errorMsg);
      }
      setTipType('error');
    } finally {
      setTimeout(() => { setTipMessage(''); setTipType(''); }, 5000);
    }
  };

  return (

  <div
   className={`story-viewer-screen ${isOwner ? 'has-bottom-bar' : ''} ${drawerOpen ? 'drawer-open' : ''} page-${pageAnimPhase} page-${pageAnimDir}`}

      onClick={onTap}
      onMouseDown={onHoldStart}
      onMouseUp={onHoldEnd}
      onTouchStart={onTouchStartCombined}
      onTouchMove={onTouchMoveCombined}
      onTouchEnd={onTouchEndCombined}
      onTouchCancel={onTouchCancelCombined}
    >
      {/* ⭐ Segmented progress at the absolute very top (one segment per slide/doc) */}
      <div className="progress-wrap">
        {slides.map((_, idx) => {
          const filled =
            idx < activeIndex ? 100 :
            idx === activeIndex ? progress :
            0;
          return (
            <div className="segment" key={`${_.__key || _.createdAt || idx}-${idx}`}>
              <div className="fill" style={{ width: `${filled}%` }} />
            </div>
          );
        })}
      </div>

      {/* Header row BELOW the progress bar */}
      <div className="story-top-bar">
        <div className="story-left no-tap">
          <img
            src={backIcon}
            alt="Back"
            className="story-back-icon"
            onClick={async (e) => {
              e.stopPropagation();
              log('nav:back-icon', { viewedAll });
              if (viewedAll) {
                await markAllSeenOnce('back-icon'); // only if all viewed
              }
 
             navigate('/home', { replace: true });

            }}
          />

          <button
            type="button"
            className="story-user-info as-link no-tap"
            onClick={goToOwnerProfile}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToOwnerProfile(e);
              }
            }}
            aria-label={`Open @${storyOwnerUsername}'s profile`}
            title={`@${storyOwnerUsername}`}
          >

<img
  src={userInfo.profilePic}
  alt=""
  className={`story-user-avatar ${isAdminAccount || isAppAccount ? 'story-user-avatar-admin' : ''}`}
/>

 
<div className="story-user-meta">
  <div className="story-user-name-row">
    <span className="story-name-with-admin">
      <span className="story-user-name">{displayName}</span>

      {(isAdminAccount || isAppAccount) && (
        <img
          src={goldTickIcon}
          alt={isAdminAccount ? 'Admin verified' : 'App verified'}
          className="story-gold-tick"
          title={isAdminAccount ? (ownerAdminRole || 'Admin') : 'App account'}
        />
      )}
    </span>

    {timeAgoLabel && (
      <>
        <span className="story-dot">•</span>
        <span className="story-user-time">{timeAgoLabel}</span>
      </>
    )}
  </div>
</div>

          </button>
        </div>

        {isOwner ? (
          <div className="story-actions no-tap">
            <button
              type="button"
              className="story-menu-btn"
              onClick={(e) => {
                e.stopPropagation();
                log('menu:open');
                setShowStoryMenuDrawer(true);
              }}
              aria-label="Open story menu"
              title="More"
            >
              <img src={dotsIcon} alt="" className="story-dots-icon" />
            </button>
          </div>
        ) : (
          <div style={{ width: 24, height: 24 }} />
        )}
      </div>

      {/* Media */}
      <div className="story-media-container">
        <div className="story-frame">
          {/* optional blurred backdrop for images */}
          {current.contentType === 'image' && current.imageUrl && (
            <img
              src={current.imageUrl}
              alt=""
              className="story-backdrop"
              aria-hidden
              draggable={false}
            />
          )}

          {/* video */}
          {current.contentType === 'video' && current.videoUrl && (
            <video
              src={current.videoUrl}
              className="story-media-video"
              autoPlay
              muted
              playsInline
            />
          )}

          {/* image */}
          {current.contentType === 'image' && current.imageUrl && (
            <img
              src={current.imageUrl}
              alt=""
              className="story-media-img"
              draggable={false}
            />
          )}


{/* text slide */}
{current.contentType === 'text' && (
  <div
    className="story-text-only"
    style={{
      backgroundColor:
        currentTextTheme.backgroundColor,

      color:
        currentTextTheme.textColor,
    }}
  >
    <div className="story-text-only-content">
      {current.storyText}
    </div>
  </div>
)}


          {/* caption overlay */}
          {current.contentType !== 'text' && !!current.caption && (
            <div className="story-overlay">
              <div className="story-gradient" />
              <p className="story-caption">{current.caption}</p>
            </div>
          )}
        </div>
      </div>

      {/* Viewer bottom input (hidden when a drawer is open) */}
      {!isOwner && !drawerOpen && (
        <div className="story-bottom-input no-tap" onClick={(e) => e.stopPropagation()}>
          {/* Left: comment input */}
          <form
            className="story-comment-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const text = commentText.trim();
              if (!text || !current?._docId || !userInfo?.username || !currentUser?.username) return;

              try {
                setSending(true);
                log('comment:send:start', { to: userInfo.username, storyDoc: current._docId });
                await api.post('/messages', {
                  from: currentUser.username,
                  to: userInfo.username,
                  type: 'shared_post',
                  message: text,
                  post: {
                    ...buildStoryCard(),
                    text,
                  },
                });
                setCommentText('');

                setTipMessage('Comment sent successfully');
                setTipType('success');
                log('comment:send:success');
                setTimeout(() => { setTipMessage(''); setTipType(''); }, 3000);

} catch (err) {
  const errorMsg =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.response?.data?.msg ||
    err?.message ||
    '';

  const lower = String(errorMsg).toLowerCase();

  log('comment:send:error', err?.response?.data || err?.message);

  if (
    lower.includes('blocked') ||
    lower.includes('you cannot message') ||
    lower.includes('cannot send message') ||
    lower.includes('cannot send comment')
  ) {
    setTipMessage('You cannot send comment to this user.');
  } else {
    setTipMessage('Could not send comment. Please try again.');
  }

  setTipType('error');

  setTimeout(() => {
    setTipMessage('');
    setTipType('');
  }, 3000);
} finally {
  setSending(false);
}

            }}
          >
            <div
              className={`story-input-wrap ${commentText.trim() ? 'has-text' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                className="story-comment-input"
                type="text"
                placeholder="Write a comment…"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                maxLength={1000}
              />

              <button
                type="submit"
                className="story-send-icon-btn"
                disabled={sending || !commentText.trim()}
                aria-label="Send comment"
                onClick={(e) => e.stopPropagation()}
              >
                <img src={sendIcon} alt="" className="story-send-icon" />
              </button>
            </div>
          </form>

          {/* Right: quick actions (Tip now, Like later) */}
          <div className="story-quick-actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="story-tip-btn"
              aria-label="Tip"
              onClick={() => { log('tips:drawer:open'); setShowTipPopup(true); }}
              title="Send tip"
            >
              <img src={tipIcon} alt="Tip" className="story-tip-icon" />
            </button>

            {tipCount > 0 && (
              <span className="qa-count" onClick={openTipList} title="View tips">
                {tipCount}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ✅ Fixed bottom bar (owner-only) */}
      {isOwner && !drawerOpen && (
        <div className="story-bottom-bar no-tap owner-stats-bar">
          <span
            className="views-label"
            role="button"
            tabIndex={0}
            title="Viewers"
            onClick={(e) => { e.stopPropagation(); log('viewers:drawer:open'); setShowViewersDrawer(true); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowViewersDrawer(true);
              }
            }}
          >
            Views: {current?.viewsCount ?? 0}
          </span>

          <span
            className="tips-label"
            role="button"
            tabIndex={0}
            title="View tips"
            onClick={(e) => { e.stopPropagation(); openTipList(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openTipList();
              }
            }}
          >
            Tips: {tipCount}
          </span>
        </div>
      )}

      {/* 💸 Tip drawers */}


{showTipPopup && (
  <div className="tip-drawer-layer no-tap" onClick={(e) => e.stopPropagation()}>
    <TipDrawer
      onClose={() => { log('tips:drawer:close'); setShowTipPopup(false); }}
      onSend={handleSendTip}
      disableHistoryTrap={true} 
    />
  </div>
)}



{showTipListDrawer && (
  <div className="tip-drawer-layer no-tap" onClick={(e) => e.stopPropagation()}>
    <TipListDrawer
      onClose={() => { log('tips:list:drawer:close'); setShowTipListDrawer(false); }}
      tips={tipList}
      currentUser={currentUser?.username}
      disableHistoryTrap={true} 
    />
  </div>
)}



      {/* 👀 Viewers drawer (owner) */}
      {showViewersDrawer && (
        <div className="tip-drawer-layer no-tap" onClick={(e) => e.stopPropagation()}>
          <ViewersDrawer
            open={showViewersDrawer}
            onClose={() => { log('viewers:drawer:close'); setShowViewersDrawer(false); }}
            storyId={current?._docId || null}
          />
        </div>
      )}

      {tipMessage && (
        <div className={`tip-banner ${tipType} no-tap`}>
          {tipMessage}
        </div>
      )}

      {showDeleteDrawer && (
        <div className="tip-drawer-layer no-tap" onClick={(e) => e.stopPropagation()}>
          <StoryDeleteDrawer
            isOpen={showDeleteDrawer}
            onClose={() => { log('delete:closeDrawer'); setShowDeleteDrawer(false); }}
            onConfirm={async () => {
              setShowDeleteDrawer(false);
              await performDelete();
            }}
          />
        </div>
      )}

      <StoryMenuDrawer
        isOpen={showStoryMenuDrawer}
        onClose={() => { log('menu:close'); setShowStoryMenuDrawer(false); }}
        canDelete={!!current._canDelete}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default StoryViewerScreen;
